import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { PhoneNumberValidator } from '~domain/validators/core/basic-types/phone-number-validator';
import { PhoneNumberFormField } from '~frontend-common/components/form-fields';
import { PhoneNumberFormConverter } from '~frontend-common/form-converters/basic-types/phone-number-form-converter';
import { useTranslator } from '~frontend-common/i18n/translator';
import { userService } from '~web-portal/services';
import { DetailRow } from './detail-row';
import { EditActions } from './edit-actions';

interface PhoneNumberFormProps {
  value: string;
}

interface FormValues {
  phoneNumber: string;
}

const FIELD_NAME = 'phoneNumber';

export function PhoneNumberForm({ value }: PhoneNumberFormProps) {
  const translator = useTranslator();
  const { tl } = translator;

  const inputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);

  const updatePhoneNumberMutation = useMutation({
    mutationFn: (phoneNumber: string) => userService.updatePhoneNumber(phoneNumber),
    onSuccess: () => {
      setIsEditing(false);
    },
  });

  const initialValues = useMemo<FormValues>(() => {
    const formConverter = new PhoneNumberFormConverter(translator);
    return {
      phoneNumber: formConverter.convertObject(value) as string,
    };
  }, [value]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  const validate = (values: FormValues) => {
    const contentValidator = new PhoneNumberValidator();
    const formValue = values[FIELD_NAME];
    const convertedValue = new PhoneNumberFormConverter(translator).convertFormContent(formValue);
    const error = contentValidator.validate(convertedValue);

    return {
      [FIELD_NAME]: error && tl(error as string),
    };
  };

  const handleEditPhone = (values: FormValues) => {
    const phoneNumber = new PhoneNumberFormConverter(translator).convertFormContent(values[FIELD_NAME]);
    updatePhoneNumberMutation.mutate(phoneNumber);
  };

  return (
    <Form
      key={`${value}-${isEditing}`} // this resets the form when undo'ing
      onSubmit={handleEditPhone}
      initialValues={initialValues}
      validate={validate}
      render={({ handleSubmit, errors }: FormRenderProps) => {
        return (
          <DetailRow>
            <form onSubmit={handleSubmit}>
              <PhoneNumberFormField
                name={FIELD_NAME}
                inputRef={inputRef}
                label={tl('profile.fields.phoneNumber')}
                disabled={!isEditing || updatePhoneNumberMutation.isLoading}
                forceErrorMessage={updatePhoneNumberMutation.isError && tl('error.unknownError')}
                endIcon={
                  <EditActions
                    name="phone-number"
                    inplaceEdit
                    isEditing={isEditing}
                    isLoading={updatePhoneNumberMutation.isLoading}
                    canConfirm={!errors[FIELD_NAME]}
                    onEdit={() => setIsEditing(true)}
                    onUndo={() => {
                      setIsEditing(false);
                      updatePhoneNumberMutation.reset();
                    }}
                  />
                }
              />
            </form>
          </DetailRow>
        );
      }}
    />
  );
}
